import { Button, Form, Input } from '@bancobarigui/barihaus';
import { Table, Space, Modal, Switch, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { ProtectedRouteContainer } from '@/components/ProtectedRouteContainer';
import { PageHeader } from '@/components/PageHeader';
import { ServiceCheck, FeatureAlert } from '@/shared/components';
import { useInspectionTypeList } from './useInspectionTypeList';
import type { InspectionType } from '@/services/types/user.types';

export function InspectionTypeList() {
  const {
    inspectionTypes,
    loading,
    isModalOpen,
    editingItem,
    submitting,
    form,
    handleNew,
    handleEdit,
    handleDelete,
    handleNameChange,
    handleModalCancel,
    handleModalSubmit,
  } = useInspectionTypeList();

  const tableColumns = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: InspectionType, b: InspectionType) => a.name.localeCompare(b.name),
    },
    {
      title: 'Código',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Requer Inspetor',
      dataIndex: 'requiresInspector',
      key: 'requiresInspector',
      render: (requiresInspector: boolean) => (
        <Tag color={requiresInspector ? 'blue' : 'orange'} style={{ margin: 0 }}>
          {requiresInspector ? 'Sim' : 'Laudo Automático'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'} style={{ margin: 0 }}>
          {isActive ? 'Ativo' : 'Inativo'}
        </Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 200,
      render: (_: unknown, record: InspectionType) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Excluir
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <ProtectedRouteContainer
      pageHeader={
        <PageHeader
          title="Tipos de Inspeção"
          actions={
            <Button
              icon={<PlusOutlined />}
              onClick={handleNew}
              className="!border-0"
              style={{ backgroundColor: '#0090ff', color: '#fff', boxShadow: 'none' }}
            >
              Novo Tipo de Inspeção
            </Button>
          }
        />
      }
    >
      <FeatureAlert services={['user']} />
      <ServiceCheck services={['user']} featureName="Tipos de Inspecao">
        <div className="flex flex-col gap-4">
        <Table
          columns={tableColumns}
          dataSource={inspectionTypes}
          loading={loading}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total de ${total} tipos`,
          }}
          scroll={{ x: 1000 }}
        />

        <Modal
          title={editingItem ? 'Editar Tipo de Inspeção' : 'Novo Tipo de Inspeção'}
          open={isModalOpen}
          onCancel={handleModalCancel}
          footer={
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
              <Button
                onClick={handleModalCancel}
                style={{ borderColor: '#101010', color: '#101010', backgroundColor: 'transparent', boxShadow: 'none' }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleModalSubmit}
                loading={submitting}
                className="!border-0"
                style={{ backgroundColor: '#0090ff', color: '#fff', boxShadow: 'none' }}
              >
                {editingItem ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          }
        >
          <Form
            form={form}
            layout="vertical"
            className="mt-4"
          >
            <div style={{ display: 'flex', gap: 32, marginBottom: 16 }}>
              <Form.Item
                name="requiresInspector"
                label="Requer Inspetor"
                valuePropName="checked"
                tooltip="Desmarque para Laudo Automático - a Prestadora preenche diretamente sem inspetor"
                style={{ marginBottom: 0 }}
              >
                <Switch />
              </Form.Item>

              {editingItem && (
                <Form.Item
                  name="isActive"
                  label="Tipo ativo no sistema"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Switch />
                </Form.Item>
              )}
            </div>

            <Form.Item
              name="name"
              label="Nome"
              rules={[{ required: true, message: 'Por favor, informe o nome' }]}
            >
              <Input
                placeholder="Nome do tipo de inspeção"
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </Form.Item>

            <Form.Item
              name="code"
              label="Código"
              rules={[{ required: true, message: 'Por favor, informe o código' }]}
            >
              <Input placeholder="Código do tipo de inspeção" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Descrição"
            >
              <Input placeholder="Descrição (opcional)" />
            </Form.Item>
          </Form>
        </Modal>
        </div>
      </ServiceCheck>
    </ProtectedRouteContainer>
  );
}
